import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import type { FormControl as FormControlType } from '@angular/forms';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { DonorFormComponent } from '../donor-form/donor-form.component';
import { PaymentElementViewComponent } from '../shared/payment-element/view.component';
import { LoaderComponent } from '../shared/loader/loader.component';
import { FeeReviewModalComponent } from '../shared/fee-review/modal.component';
import { LanguageSelectorComponent } from '../shared/language-selector/language-selector.component';
import { CheckoutSummaryComponent } from '../shared/checkout/summary.component';

import { selectSelectedCampaignVMByLang } from '../../state/campaing/selectors';
import { CampaignsActions } from '../../state/campaing/actions';
import {
  DonationFlowUiActions,
  acceptFeeReview,
  cancelFeeReview,
  selectDeferFirstCharge,
  hideFeeReview,
  selectCheckoutSummaryVM,
  selectCustomer,
  selectDonationMetadata,
  selectDonationType,
  selectFeeReviewVm,
  selectFirstPaymentDate,
  selectIsLoading,
  selectPriceDraft,
  selectSetupIntent,
  useAnotherCard,
} from '../../state/donation-flow';

import {
  OneTimePaymentActions,
  RecurringPaymentActions,
} from '../../state/payment/actions';
import {
  selectOTActive,
  selectOTIsConfirming,
  selectOTIsReady,
  selectRActive,
  selectRIsConfirming,
  selectRIsReady,
} from '../../state/payment/selectors';

import {
  CheckoutSummaryVM,
  DonationType,
  DonorFormValue,
  PriceDraft,
} from '../../types/donation-flow.models';
import {
  DonationRouteData,
  isValidStepFor,
} from '../../types/donation-routes';
import {
  buildCustomerRequest,
  buildDonationMetadata,
  buildDonorFormValue,
} from '../../types/donor-mappers';
import { ModalNavigationService } from '../../services/modal-navigation.service';
import { ModalLayoutService } from '../../services/modal-layout.service';

type DonationView = 'details' | 'summary' | 'payment';
type StepperTarget = 'amount' | DonationView;

/**
 * Unified step-2 component for both OneTime and Recurring flows.
 *
 * The donation type is read once from route data and validated against the
 * NgRx store: no derived boolean, no silent fallback, no duplicated template.
 * All type-specific decisions (container id, confirm label, payment actions
 * and selectors) are resolved by a small `switch` on `DonationType`.
 */
@Component({
  selector: 'don-donation-details',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    DonorFormComponent,
    PaymentElementViewComponent,
    LoaderComponent,
    LanguageSelectorComponent,
    FeeReviewModalComponent,
    CheckoutSummaryComponent,
  ],
  templateUrl: './donation-details.component.html',
  styleUrls: ['./donation-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class DonationDetailsComponent {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly modalNav = inject(ModalNavigationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly layout = inject(ModalLayoutService);

  /** Resolved once from route data and validated against the store. */
  readonly DonationType = DonationType;
  readonly donationType: DonationType;
  readonly containerId: string;
  readonly confirmLabelKey: string;

  readonly selectedLanguage = new FormControl('en', Validators.required);
  currentView: DonationView = 'details';

  private pendingStepperTarget: 'summary' | 'payment' | null = null;

  private readonly currencyPipe = new CurrencyPipe('en-US');

  @ViewChild('paymentHeading')
  private paymentHeading?: ElementRef<HTMLHeadingElement>;

  @ViewChild('summaryHeading')
  private summaryHeading?: ElementRef<HTMLHeadingElement>;

  @ViewChild(DonorFormComponent)
  private donorFormComponent?: DonorFormComponent;

  get displayNameControl(): FormControlType | null {
    return (this.donorFormComponent?.form.get('displayName') as FormControlType) ?? null;
  }

  get isCompanyControl(): FormControlType | null {
    return (this.donorFormComponent?.form.get('isCompany') as FormControlType) ?? null;
  }

  private readonly paymentElementVisibleSubject = new BehaviorSubject<boolean>(false);
  readonly paymentElementVisible$ = this.paymentElementVisibleSubject.asObservable();

  readonly clientSecret$: Observable<string | null>;
  readonly active$: Observable<boolean>;
  readonly isReady$: Observable<boolean>;
  readonly isConfirming$: Observable<boolean>;

  readonly isLoading$ = this.store.select(selectIsLoading);
  readonly feeReview$ = this.store.select(selectFeeReviewVm);
  readonly isFirstChargeDeferred$ = this.store.select(selectDeferFirstCharge);
  readonly firstPaymentDate$ = this.store.select(selectFirstPaymentDate);

  readonly donorFormValue$ = combineLatest([
    this.store.select(selectCustomer),
    this.store.select(selectDonationMetadata),
  ]).pipe(
    map(([customer, metadata]) => buildDonorFormValue(customer, metadata)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly summaryVm$ = this.store
    .select(selectCheckoutSummaryVM)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  /** "Donate $XX.XX USD" (or "/mo" for recurring), recomputed on totals/language change. */
  readonly confirmLabel$ = combineLatest([
    this.summaryVm$,
    this.translate.onLangChange.pipe(startWith(null)),
  ]).pipe(
    map(([vm]) => {
      const total =
        vm.hasCalculatedFees && vm.donationFees !== undefined
          ? vm.feesCovered
            ? vm.donationAmount + vm.donationFees
            : vm.donationAmount
          : vm.donationAmount;
      const currency = (vm.currency || 'USD').toUpperCase();
      const formatted =
        this.currencyPipe.transform(total, currency, 'symbol', '1.2-2') ?? '';
      const amount = `${formatted} ${currency}`;
      const key =
        vm.donationType === DonationType.Recurring
          ? 'CAMPAIGN.Actions.DonateAmountMonthly'
          : 'CAMPAIGN.Actions.DonateAmount';
      return this.translate.instant(key, { amount });
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly selectedCampaign$ = this.selectedLanguage.valueChanges.pipe(
    startWith(this.selectedLanguage.value),
    switchMap((lang) =>
      this.store
        .select(selectSelectedCampaignVMByLang, { lang })
        .pipe(shareReplay({ bufferSize: 1, refCount: true }))
    )
  );

  constructor() {
    this.layout.setSizeUntilDestroyed('donation-details', 'wide', this.destroyRef);

    // Guards against landing on this step without campaigns loaded/selected
    // (e.g. page reload or direct navigation): loading is a no-op if the
    // campaigns are already in the store, and auto-selects one if missing.
    this.store.dispatch(CampaignsActions.load());

    const routeData = this.readRouteData();

    if (!routeData || !isValidStepFor(routeData.donationType, routeData.step)) {
      // Route data is a runtime guarantee via `satisfies`, but we still guard
      // for anyone crafting the URL by hand.
      this.donationType = DonationType.OneTime;
      this.containerId = 'pe-one-time';
      this.confirmLabelKey = 'CAMPAIGN.Actions.DonateOnce';
      this.clientSecret$ = this.buildClientSecretStream();
      this.active$ = this.buildActiveStream();
      this.isReady$ = this.store.select(selectOTIsReady);
      this.isConfirming$ = this.store.select(selectOTIsConfirming);
      this.redirectToStart();
      return;
    }

    this.donationType = routeData.donationType;
    this.containerId =
      this.donationType === DonationType.Recurring
        ? 'pe-recurring'
        : 'pe-one-time';
    this.confirmLabelKey =
      this.donationType === DonationType.Recurring
        ? 'CAMPAIGN.Actions.DonateMonthly'
        : 'CAMPAIGN.Actions.DonateOnce';

    this.clientSecret$ = this.buildClientSecretStream();
    this.active$ = this.buildActiveStream();
    this.isReady$ =
      this.donationType === DonationType.Recurring
        ? this.store.select(selectRIsReady)
        : this.store.select(selectOTIsReady);
    this.isConfirming$ =
      this.donationType === DonationType.Recurring
        ? this.store.select(selectRIsConfirming)
        : this.store.select(selectOTIsConfirming);

    this.selectedLanguage.setValue(localStorage.getItem('lang') || 'en');
    this.bindClientSecretToPaymentSlice();
    this.guardAgainstStoreMismatch();
  }

  onFeeAccept(): void {
    this.store.dispatch(acceptFeeReview());
    this.store.dispatch(hideFeeReview());
  }

  onUseAnotherCard(): void {
    this.store.dispatch(useAnotherCard());
  }

  onFeeCancel(): void {
    this.store.dispatch(cancelFeeReview());
    this.store.dispatch(hideFeeReview());
  }

  changeLanguage(lang: string): void {
    this.translate.use(lang);
    this.selectedLanguage.setValue(lang);
  }

  onCampaignImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.src.indexOf('banner.webp') === -1) {
      img.src = 'assets/Image/Story/banner.webp';
    }
  }
  
  getLocalizedCampaignName(campaign: { nameEs?: string; nameEn?: string }): string {
    const name =
      this.selectedLanguage.value === 'es' ? campaign.nameEs : campaign.nameEn;
  
    return this.normalizeCampaignText(name);
  }

  onViewEntered(id: string): void {
    switch (this.donationType) {
      case DonationType.OneTime:
        this.store.dispatch(OneTimePaymentActions.viewEntered({ containerId: id }));
        return;
      case DonationType.Recurring:
        this.store.dispatch(RecurringPaymentActions.viewEntered({ containerId: id }));
        return;
    }
  }

  onViewExited(): void {
    switch (this.donationType) {
      case DonationType.OneTime:
        this.store.dispatch(OneTimePaymentActions.viewExited());
        return;
      case DonationType.Recurring:
        this.store.dispatch(RecurringPaymentActions.viewExited());
        return;
    }
  }

  onConfirmClicked(): void {
    switch (this.donationType) {
      case DonationType.OneTime:
        this.store.dispatch(OneTimePaymentActions.confirmRequested());
        return;
      case DonationType.Recurring:
        this.store.dispatch(RecurringPaymentActions.confirmRequested());
        return;
    }
  }

  onSummaryVmChange(vm: CheckoutSummaryVM): void {
    this.store.dispatch(
      DonationFlowUiActions.toggleCoverFees({ coverFees: !!vm.feesCovered })
    );
  }

  onDonorFormSubmitted(v: DonorFormValue): void {
    const requestedTarget = this.pendingStepperTarget;
    this.pendingStepperTarget = null;

    const customer = buildCustomerRequest(v);

    this.store
      .select(selectPriceDraft)
      .pipe(take(1))
      .subscribe((draft: PriceDraft | null) => {
        if (!draft?.productId || !draft?.productFeeId) {
          // productId + productFeeId drive fee/net calculation on the backend
          // and are guaranteed by the campaign; missing either is a bug we
          // must not paper over by hitting the API with placeholder strings.
          console.error(
            '[DonationDetailsComponent] Missing productId/productFeeId in priceDraft. Aborting.',
            draft
          );
          this.redirectToStart();
          return;
        }

        const metadata = buildDonationMetadata(
          v,
          draft.productId,
          draft.productFeeId,
          this.donationType
        );

        this.store.dispatch(
          DonationFlowUiActions.setDonationMetadata({ metadata })
        );

        switch (this.donationType) {
          case DonationType.OneTime:
            this.store.dispatch(
              DonationFlowUiActions.beginStep2OneTime({ customer })
            );
            this.goToSummary();
            if (requestedTarget === 'payment') {
              this.goToPayment();
            }
            return;
          case DonationType.Recurring:
            this.store.dispatch(
              DonationFlowUiActions.beginStep2Recurring({ customer })
            );
            this.goToSummary();
            if (requestedTarget === 'payment') {
              this.goToPayment();
            }
            return;
        }
      });
  }

  get canOpenSummaryStep(): boolean {
    return true;
  }

  get canOpenPaymentStep(): boolean {
    return true;
  }

  onStepperClick(target: StepperTarget): void {
    switch (target) {
      case 'amount':
        this.redirectToStart();
        return;
      case 'details':
        this.backToDetails();
        return;
      case 'summary':
        if (this.currentView === 'details') {
          if (!this.validateDonorFormForStepper()) {
            return;
          }
          this.pendingStepperTarget = 'summary';
          this.donorFormComponent?.onSubmit();
          return;
        }
        this.backToSummary();
        return;
      case 'payment':
        if (this.currentView === 'details') {
          if (!this.validateDonorFormForStepper()) {
            return;
          }
          this.pendingStepperTarget = 'payment';
          this.donorFormComponent?.onSubmit();
          return;
        }
        this.goToPayment();
        return;
    }
  }

  private validateDonorFormForStepper(): boolean {
    const form = this.donorFormComponent?.form;
    if (!form) {
      return false;
    }

    if (form.invalid) {
      this.donorFormComponent!.submitted = true;
      form.markAllAsTouched();
      return false;
    }

    return true;
  }

  goToSummary(): void {
    this.currentView = 'summary';
    queueMicrotask(() => this.summaryHeading?.nativeElement.focus());
  }

  goToPayment(): void {
    this.resetPaymentConfirmState();
    this.currentView = 'payment';
    // Defensive remount trigger for navigation-heavy flows (back/forward/use-another-card).
    // Mount effect is idempotent and ignores this when already mounted for the same secret.
    this.onViewEntered(this.containerId);
    queueMicrotask(() => this.paymentHeading?.nativeElement.focus());
  }

  backToDetails(): void {
    if (this.currentView === 'payment') {
      this.resetPaymentConfirmState();
    }

    this.currentView = 'details';
  }

  backToSummary(): void {
    if (this.currentView === 'payment') {
      this.resetPaymentConfirmState();
    }

    this.currentView = 'summary';
  }

  goBack(): void {
    this.modalNav.smartBack();
  }

  private readRouteData(): DonationRouteData | null {
    const data = this.route.snapshot.data as Partial<DonationRouteData>;
    const donationType = data?.donationType;
    const step = data?.step;
    if (
      (donationType === DonationType.OneTime ||
        donationType === DonationType.Recurring) &&
      typeof step === 'number'
    ) {
      return { donationType, step };
    }
    return null;
  }

  private buildClientSecretStream(): Observable<string | null> {
    return this.store.select(selectSetupIntent).pipe(
      map((si) => si?.client_secret ?? null),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private buildActiveStream(): Observable<boolean> {
    return combineLatest([this.clientSecret$, this.paymentElementVisible$]).pipe(
      map(([cs, visible]) => !!cs && visible),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Re-mounts Stripe Payment Element whenever the client secret changes.
   * We tear down first, then push the new secret in a microtask so Stripe's
   * internal iframe is fully unmounted before mounting again (this replaces
   * the previous `setTimeout` with an arbitrary number).
   */
  private bindClientSecretToPaymentSlice(): void {
    this.clientSecret$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cs) => {
        const setSecret = (clientSecret: string | null) =>
          this.donationType === DonationType.Recurring
            ? RecurringPaymentActions.setClientSecret({ clientSecret })
            : OneTimePaymentActions.setClientSecret({ clientSecret });

        const setActive = (active: boolean) =>
          this.donationType === DonationType.Recurring
            ? RecurringPaymentActions.setActive({ active })
            : OneTimePaymentActions.setActive({ active });

        this.store.dispatch(setSecret(cs));
        this.paymentElementVisibleSubject.next(false);
        this.store.dispatch(setActive(false));

        queueMicrotask(() => {
          const shouldRender = !!cs;
          this.paymentElementVisibleSubject.next(shouldRender);
          this.store.dispatch(setActive(shouldRender));
          if (shouldRender) {
            this.onViewEntered(this.containerId);
          }
        });
      });
  }

  /**
   * If the route says OneTime but the store says Recurring (or vice-versa)
   * we refuse to bootstrap Stripe and send the donor back to step 1.
   */
  private guardAgainstStoreMismatch(): void {
    this.store
      .select(selectDonationType)
      .pipe(take(1))
      .subscribe((storeType) => {
        if (storeType && storeType !== this.donationType) {
          this.redirectToStart();
        }
      });
  }

  private redirectToStart(): void {
    // Outlet vs `/donation-custom` is handled by the nav service.
    this.modalNav.navigateToPaymentStep(this.donationType, 1);
  }

  private resetPaymentConfirmState(): void {
    switch (this.donationType) {
      case DonationType.OneTime:
        this.store.dispatch(OneTimePaymentActions.confirmReset());
        return;
      case DonationType.Recurring:
        this.store.dispatch(RecurringPaymentActions.confirmReset());
        return;
    }
  }

  private normalizeCampaignText(value?: string | null): string {
    if (!value) {
      return '';
    }
  
    return value.replace(/Creciendo\s+Juntoss/gi, 'Creciendo Juntos');
  }
}
